import React from 'react';
/**
 *
 * STANDARD COMPONENTS
 *
 * */
import App, { AppProps } from './components/app/App';
import Auth, { AuthProps } from './pages/auth/Auth';
import ErrorBoundary from './components/errorboundary/ErrorBoundary';
import Footer, { FooterProps } from './components/footer/Footer';
import Header, { HeaderProps } from './components/header/Header';
import Loading, { LoadingProps } from './components/loading/Loading';

/**
 *
 * ASYNC / REACT LAZY COMPONENT
 *
 * */

type ComponentClassAsync<T> = Promise<{ default: React.ComponentClass<T> }>;

export class ComponentFactory {
  static get<T>(component: any): new () => React.Component<T> {
    return component;
  }

  static getAsync<T>(component: any): ComponentClassAsync<T> {
    return component;
  }

  static get AccountSelectAsync() {
    return import(/* webpackChunkName: "AccountSelect" */ './components/accountselect/AccountSelect');
  }

  static get App() {
    return ComponentFactory.get<AppProps>(App);
  }

  static get Auth() {
    return ComponentFactory.get<AuthProps>(Auth);
  }

  static get DocumentsAsync() {
    return import(/* webpackChunkName: "Documents" */ './pages/documents/Documents');
  }

  static get SettingsAsync() {
    return import(/* webpackChunkName: "Settings" */ './pages/settings/Settings');
  }

  static get BankAccountFormAsync() {
    return import(/* webpackChunkName: "BankAccountForm" */ './components/bankaccountform/BankAccountForm');
  }

  static get InfoCardAsync() {
    return import(/* webpackChunkName: "InfoCard" */ './components/infocard/InfoCard');
  }

  static get DocumentItemAsync() {
    return import(/* webpackChunkName: "DocumentItem" */ './components/documentitem/DocumentItem');
  }

  static get DocumentsListAsync() {
    return import(/* webpackChunkName: "DocumentsList" */ './components/documentslist/DocumentsList');
  }

  static get EmptyAccountsAsync() {
    return import(/* webpackChunkName: "EmptyAccounts" */ './components/emptyaccounts/EmptyAccounts');
  }

  static get ErrorBoundary() {
    return ComponentFactory.get(ErrorBoundary);
  }

  static get ErrorPageAsync() {
    return import(/* webpackChunkName: "ErrorPage" */ './pages/errorpage/ErrorPage');
  }

  static get Footer() {
    return ComponentFactory.get<FooterProps>(Footer);
  }

  static get Header() {
    return ComponentFactory.get<HeaderProps>(Header);
  }

  static get HelpAsync() {
    return import(/* webpackChunkName: "Help" */ './pages/help/Help');
  }

  static get HomeAsync() {
    return import(/* webpackChunkName: "Home" */ './pages/home/Home');
  }

  static get InvestAsync() {
    return import(/* webpackChunkName: "Invest" */ './pages/invest/Invest');
  }

  static get Loading() {
    return ComponentFactory.get<LoadingProps>(Loading);
  }

  static get OverviewAsync() {
    return import(/* webpackChunkName: "Overview" */ './pages/overview/Overview');
  }

  static get PortfolioAsync() {
    return import(/* webpackChunkName: "Portfolio" */ './components/portfolio/Portfolio');
  }

  static get FundInfoAsync() {
    return import(/* webpackChunkName: "FundInfo" */ './components/fundinfo/FundInfo');
  }

  static get SideNavAsync() {
    return import(/* webpackChunkName: "SideNav" */ './components/sidenav/SideNav');
  }

  static get TransactionItemAsync() {
    return import(/* webpackChunkName: "TransactionItem" */ './components/transactionitem/TransactionItem');
  }

  static get TransactionsListAsync() {
    return import(/* webpackChunkName: "TransactionsList" */ './components/transactionslist/TransactionsList');
  }

  static get WalletAsync() {
    return import(/* webpackChunkName: "Wallet" */ './pages/wallet/Wallet');
  }

  static get BankAccountInfoAsync() {
    return import(/* webpackChunkName: "BankAccountInfo" */ './components/bankaccountinfo/BankAccountInfo');
  }
}
